import {useState, useEffect} from 'react';
import DOMPurify from 'dompurify';
import TextField from '@mui/material/TextField';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Popover from '@mui/material/Popover';
import FieldPage from '@FormFunctionalDisplay/FieldPage';
import LeapFrogLoader from '@CommonRepresentationalDisplay/Loaders/LeapFrogLoader';
import { useParams } from 'react-router-dom';
import { validate, res } from 'react-email-validator';
import { ReactComponent as DefaultFormPic } from '@Assets/form.svg';
import { ReactComponent as FormNotFound } from '@Assets/formNotFound.svg';
import { ReactComponent as HighFive } from '@Assets/highFive.svg';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import FormUserAccessLinkService from '@Helpers/URLHelpers/FormUserAccessLinkService';
import TicketService from '@Helpers/URLHelpers/TicketService';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import { useNavigateWithRedirect } from '@Hooks/useNavigateWithRedirect';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import FieldPageService from '@Helpers/URLHelpers/FieldPageService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './FormPage.scss';


export default function FormPage(props) {

    const { formId } = useParams();
    const navigate = useNavigateWithRedirect();
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [formFieldPageList, setFormFieldPageList] = useState([]);
    const [cursor, setCursor] = useState('wait');
    const [, setEmailVerified] = useState(false);
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [fullName, setFullName] = useState('');
    const [ticketCreated, setTicketCreated] = useState(null);
    const [, setFormUserAccessList] = useState([]);
    const [, setIsUserPermitted] = useState(false);
    const [disableNext, setDisableNext] = useState(true);
    const [isNextHovered, setIsNextHovered] = useState(false);
    const [, setFormTitle] = useState();
    const [details, setDetails] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [, setFormDescription] = useState();
    const [, setImageUrl] = useState('');
    const [formFieldList, setFormFieldList] = useState([]);
    const [, setFormFieldIdList] = useState([]);
    const [, setFormFieldPageIdList] = useState([]);
    const [, setAdminFieldList] = useState([]);
    const [, setAdminFieldIdList] = useState([]);
    const [pageType, setPageType] = useState('intro');
    const [disableSubmit] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [, setFieldBackgroundObj] = useState({});
    const [, setPageHeaderObj] = useState({});
    const [fieldBackgroundStyle, setFieldBackgroundStyle] = useState({});
    const [fieldEntryMap, setFieldEntryMap] = useState({});
    const [anchorNextDisabledPopover, setAnchorNextDisabledPopover] = useState(null);
    const [pagePath, setPagePath] = useState([]);
    const { token } = useReduxToken();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const formUserAccessLinkService = new FormUserAccessLinkService();
    let fieldFilter = {
        projectIdList: [],
        tableType: 'kaiziqResolveConnection',
        filterAttribute1List: []
    };
    let { activeUser, activeUserId } = useReduxActiveUser();
    const ticketService = new TicketService();
    const fieldPageService = new FieldPageService();
    const fieldService = new FieldService();
    const errorHandler = new ErrorHandler();
    const className = 'formPage';

    useEffect(() => {
        renderData();
        if(!!token && token !== 'void') {
            setEmail(activeUser?.email);
            setFullName(activeUser?.fullName);
            setPhoneNumber(activeUser?.contactNumber);
            verifyIntroNextButton(activeUser?.email, activeUser?.contactNumber, activeUser?.fullName)
        }
    }, [props])

    const renderData = async () => {
        try {
            if(!!formId) {
                let kaiziqResolveConnectionObj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionByFormId(formId);
                kaiziqResolveConnectionObj = JSON.parse(kaiziqResolveConnectionObj.body);
                setKaiziqResolveConnection(kaiziqResolveConnectionObj);
                setFormTitle(kaiziqResolveConnectionObj.title);
                setFormDescription(kaiziqResolveConnectionObj.description);
                setImageUrl(kaiziqResolveConnectionObj.photo === undefined || kaiziqResolveConnectionObj.photo === null
                            ? ''
                            : URL.createObjectURL(dataURLtoFile(kaiziqResolveConnectionObj.photo)));

                let adminFilter = fieldFilter;
                adminFilter.filterAttribute1List = ['admin'];
                adminFilter.projectIdList = [kaiziqResolveConnectionObj?.projectId];
                adminFilter.tableId = kaiziqResolveConnectionObj?.id;
                let filter = {
                    fieldFilter: adminFilter
                }
                let adminFieldListObj = await fieldService.getListFiltered(filter);
                adminFieldListObj = JSON.parse(adminFieldListObj.body);
                setAdminFieldList(adminFieldListObj?.result);
                let formFilter = fieldFilter;
                formFilter.filterAttribute1List = ['form'];
                filter = {
                    fieldFilter: formFilter
                }
                let formFieldListObj = await fieldService.getListFiltered(filter);
                formFieldListObj = JSON.parse(formFieldListObj.body);
                setFormFieldList(formFieldListObj?.result);
                let fieldPageListObj = await fieldPageService.getFieldPageListByTable('kaiziqResolveConnection', kaiziqResolveConnectionObj.id);
                fieldPageListObj = JSON.parse(fieldPageListObj.body);
                const formPages = JSON.parse(kaiziqResolveConnectionObj?.details)?.fields?.formPages;
                const sortedFieldPageListObj = fieldPageListObj.sort((a, b) => {
                    return formPages.indexOf(a.id) - formPages.indexOf(b.id);
                });
                setFormFieldPageList(sortedFieldPageListObj);
                let detailsObj = JSON.parse(kaiziqResolveConnectionObj.details);
                setFormFieldPageIdList(detailsObj.fields['formPages'])
                setFormFieldIdList(detailsObj.fields['form']);
                setAdminFieldIdList(detailsObj.fields['admin']);
                setDetails(detailsObj);
                setPageHeaderObj(detailsObj?.settings?.pageHeader);
                let themeObj = detailsObj?.settings?.theme;
                let fieldBackgroundObjValue = themeObj?.formFieldTheme?.formFieldThemeBackground;
                setFieldBackgroundObj(fieldBackgroundObjValue);
                setFieldBackgroundStyle({
                    background: fieldBackgroundObjValue.color
                });
                if(kaiziqResolveConnectionObj.accessType === 'private_verified') {
                    let formUserAccessObj = await formUserAccessLinkService.getListByForm(kaiziqResolveConnectionObj.id);
                    formUserAccessObj = JSON.parse(formUserAccessObj.body);
                    setFormUserAccessList(formUserAccessObj);
                    setIsUserPermitted(formUserAccessObj?.some((user) => user.userId === activeUserId))
                } else if(kaiziqResolveConnectionObj.accessType === 'private_email') {
                    let formUserAccessObj = await formUserAccessLinkService.getListByForm(kaiziqResolveConnectionObj.id);
                    formUserAccessObj = JSON.parse(formUserAccessObj.body);
                    setFormUserAccessList(formUserAccessObj);
                    if(!!activeUser) {
                        setIsUserPermitted(formUserAccessObj?.some((user) => user.email === activeUser.email))
                        setEmailVerified(true);
                    } else {
                        setIsUserPermitted(true);
                    }
                } else if(kaiziqResolveConnectionObj.visibility === 'public') {
                    setIsUserPermitted(true);
                }
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    function emailValidCheck(email){
        validate(email);
        if(res){
            return true;
        } else {
            return false;
        }
    }

    const verifyIntroNextButton = (checkEmail, checkPhoneNumber, checkFullName) => {
        let isNextDisabledBoolean = false;
        if(details?.settings?.defaults?.fullName) {
            if(checkFullName?.length === 0) isNextDisabledBoolean = true;
        }
        if(details?.settings?.defaults?.email) {
            if(checkEmail?.length === 0 || !emailValidCheck(checkEmail)) isNextDisabledBoolean = true;
        }
        if(details?.settings?.defaults?.phoneNumber) {
            if(checkPhoneNumber?.length === 0) isNextDisabledBoolean = true;
        }
        setDisableNext(isNextDisabledBoolean);
        setRefresh(!refresh);
    }

    const handleNextDisabledPopoverOpen = (event) => {
        setAnchorNextDisabledPopover(event.currentTarget);
    };

    const handleNextDisabledPopoverClose = () => {
        setAnchorNextDisabledPopover(null);
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const handleSearchClick = () => {
        navigate('/home/form/search');
    };

    const handleSubmitForm = async(body) => {
        setCursor('wait');
        try {
            let accessType = kaiziqResolveConnection.accessType;
            if(accessType === 'public_open') {
                let ticketObj = {
                    ticket: {
                        kaiziqResolveConnectionId: kaiziqResolveConnection.id,
                        fieldEntryObj: fieldEntryMap,
                        createFullName: fullName,
                        createEmail: email,
                        createPhoneNumber: phoneNumber
                    }
                }
                ticketObj = await ticketService.createTicket(ticketObj);
                ticketObj = JSON.parse(ticketObj.body);
                setTicketCreated(ticketObj);
            } else if(accessType === 'public_email') {
            } else {
                if(!!activeUser && Object.keys(activeUser) !== 0) {
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleFieldEntryChange = (fieldId, value) => {
        let fieldEntryMapObj = fieldEntryMap;
        fieldEntryMapObj[fieldId] = value;
        setFieldEntryMap(fieldEntryMapObj);
    }

    const reportAnotherClick = async() => {
        setCursor('wait');
        await renderData();
        setFieldEntryMap({});
        setPageType('intro');
        setPageIndex(0);
        setFullName('');
        setEmail('');
        setPhoneNumber('');
        setTicketCreated(null);
        if(!!token && token !== 'void') {
            setEmail(activeUser?.email);
            setFullName(activeUser?.fullName);
            setPhoneNumber(activeUser?.contactNumber);
        }
        verifyIntroNextButton();
        setCursor('auto');
    }

    const handleEmailTextChange = (e) => {
        let sanitizedEmail = DOMPurify.sanitize(e.target.value);
        setEmail(sanitizedEmail);
        verifyIntroNextButton(sanitizedEmail, phoneNumber, fullName);
    }

    const onPhoneNumberChange = async(newNumber) => {
        setPhoneNumber(newNumber)
        verifyIntroNextButton(email, newNumber, fullName);
    }

    const handleFullNameTextChange = (e) => {
        let sanitizedFullName = DOMPurify.sanitize(e.target.value);
        setFullName(sanitizedFullName);
        verifyIntroNextButton(email, phoneNumber, sanitizedFullName);
    }

    const handleNextClick = async () => {
        let pagePathObj = pagePath;
        if(pageType === 'intro') {
            setPageType('pageLoader');
            setPageType('formPage');
            setPageIndex(0)
            pagePathObj = [...pagePath, -1];
        } else {
            let formFieldPageObj = formFieldPageList[pageIndex];
            let detailsObj = JSON.parse(formFieldPageObj?.details);
            let nextBehaviour = detailsObj?.buttonBehaviour?.next;
            if(nextBehaviour?.behaviour?.action === 'next') {
                setPageType('pageLoader');
                setPageIndex(pageIndex + 1);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'goto') {
                let nextPageIndex = formFieldPageList.indexOf(formFieldPageList?.find(formFieldPage => formFieldPage?.id === nextBehaviour?.nextPage?.value));
                setPageType('pageLoader');
                setPageIndex(nextPageIndex);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'conditional') {
                let formFieldObj = fieldEntryMap[nextBehaviour?.conditionalField];
                let nextOptionSelectObj = nextBehaviour?.value?.find((valueObj) => valueObj?.option?.id === JSON.parse(formFieldObj));
                let nextPageIndex = formFieldPageList.indexOf(formFieldPageList?.find(formFieldPage => formFieldPage?.id === nextOptionSelectObj?.goto?.value));
                setPageType('pageLoader');
                setPageIndex(nextPageIndex);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'submit') {
                await handleSubmitForm();
            } else {
                setPageType('pageLoader');
                setPageIndex(pageIndex + 1);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            }
        }
        setPagePath(pagePathObj);
        setIsNextHovered(false);
    }

    const handleBackClick = async() => {
        let pagePathObj = pagePath;
        let previousIndex = pagePathObj.pop();
        if(previousIndex === -1) {
            setPageType('pageLoader');
            setPageType('intro');
            setPageIndex(-1)
        } else {
            setPageType('pageLoader');
            setPageIndex(previousIndex);
            setPageType('formPage');
        }
    }

    const handleMouseOverNext = (e) => {
        setIsNextHovered(true);
        if(disableNext) {
            handleNextDisabledPopoverOpen(e);
        }
    }

    const handleTicketIdClick = () => {
        const params = new URLSearchParams({
          type: 'ticket',
          subType: 'track',
          email: email,
          ticketId: ticketCreated.id,
          trackingId: ticketCreated?.ticketId
        });
        navigate(`/home/track?${params.toString()}`);
    }

    const populatePage = () => {
        let pageBody;
        if(pageType === 'intro') {
            pageBody = <div className={className + '__inner__body__formBody__form__page flex-column-center-2vh-gap width-wfa'}>
                <div className={className + '__inner__body__formBody__form__page__inner flex-column-center-2vh-gap'}>
                    <div className={className + '__inner__body__formBody__form__page__inner__header width-wfa '} >
                        <div className={className + '__inner__body__formBody__form__page__inner__header__title flex-row-center-1vw-gap'}>
                            <div className={className + '__inner__body__formBody__form__page__inner__header__title__logo'}>
                                {
                                    !!kaiziqResolveConnection?.photo ?
                                    <img
                                        src={URL.createObjectURL(dataURLtoFile(kaiziqResolveConnection?.photo))}
                                        className={className + '__inner__body__formBody__form__page__inner__header__title__logo__photo'}
                                        alt='Form Thumbnail'/> :
                                    <DefaultFormPic className={className + '__inner__body__formBody__form__page__inner__header__title__logo__photo opacity-0p6'}/>
                                }
                            </div>
                            {kaiziqResolveConnection?.title}
                        </div>
                        <div className={className + '__inner__body__formBody__form__page__inner__header__description'}
                            dangerouslySetInnerHTML={{__html: kaiziqResolveConnection?.description}}/>
                    </div>
                    <div className={className + '__inner__body__formBody__form__page__inner__fields width-wfa flex-column-flex-start-2vh-gap'}>
                        {
                            details?.settings?.defaults?.fullName &&
                                <div className={className + '__inner__body__formBody__form__page__inner__fields__fullName flex-column-flex-start-2vh-gap width-wfa'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__fields__fullName__t1 standard-text-weight ' +
                                        ((isNextHovered && fullName?.length === 0) ? 'red-text' : '')}>
                                        Name
                                    </div>
                                    <TextField
                                        className={className + '__inner__body__formBody__form__page__inner__fields__fullName__textField width-wfa'}
                                        placeholder='Enter Your Name ...'
                                        type='text'
                                        required
                                        onChange={handleFullNameTextChange}
                                        value={fullName}
                                        inputProps={{ maxLength: 500 }}
                                        maxRows='1'
                                        variant='standard'/>
                                </div>
                        }
                        {
                            details?.settings?.defaults?.email &&
                                <div className={className + '__inner__body__formBody__form__page__inner__fields__email flex-column-flex-start-2vh-gap width-wfa'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__fields__email__t1 standard-text-weight ' +
                                        ((isNextHovered && (email?.length === 0 || !emailValidCheck(email))) ? 'red-text' : '')}>
                                        Email ID
                                    </div>
                                    <TextField
                                        className={className + '__inner__body__formBody__form__page__inner__fields__fullName__textField width-wfa'}
                                        placeholder='Enter Your Email ID ...'
                                        type='text'
                                        required
                                        onChange={handleEmailTextChange}
                                        value={email}
                                        inputProps={{ maxLength: 500 }}
                                        maxRows='1'
                                        variant='standard'/>
                                </div>
                        }
                        {
                            details?.settings?.defaults?.phoneNumber &&
                                <div className={className + '__inner__body__formBody__form__page__inner__fields__phoneNumber flex-column-flex-start-2vh-gap width-wfa'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__fields__phoneNumber__t1 standard-text-weight ' +
                                        ((isNextHovered && phoneNumber?.length < 10) ? 'red-text' : '')}>
                                        Contact Number
                                    </div>
                                    <div className={className + '__inner__body__formBody__form__page__inner__fields__phoneNumber'}>
                                        <PhoneInput
                                            country='in'
                                            placeholder='Enter contact number'
                                            value={phoneNumber}
                                            specialLabel=''
                                            disabled={cursor === 'wait'}
                                            onChange={(newNumber) => onPhoneNumberChange(newNumber)}/>
                                    </div>
                                </div>
                        }
                    </div>
                    <div className={className + '__inner__body__formBody__form__page__inner__buttons width-wfa flex-row-center-space-between'}>
                        <div className={className + '__inner__body__formBody__form__page__inner__buttons__left flex-row-center-1vw-gap'}>
                            <div className={className + '__inner__body__formBody__form__page__inner__buttons__left__startAgain standard-link'}
                                onClick={reportAnotherClick}>
                                Clear Form
                            </div>
                        </div>
                        <div className={className + '__inner__body__formBody__form__page__inner__buttons__right flex-row-center-1vw-gap'}>
                            <div className={className + '__inner__body__formBody__form__page__inner__buttons__right__next '}
                                onMouseEnter={handleMouseOverNext}
                                onMouseLeave={handleNextDisabledPopoverClose}>
                                <BorderWhiteButtonV2
                                    text='Next'
                                    disabled={disableNext}
                                    onClick={handleNextClick}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        } else if(pageType === 'formPage') {
            pageBody = <div className={className + '__inner__body__formBody__form__page flex-column-center-2vh-gap width-wfa'}>
                <div className={className + '__inner__body__formBody__form__page__inner flex-column-center-2vh-gap'}>
                    <div className={className + '__inner__body__formBody__form__page__inner__fieldList width-wfa'}>
                        <FieldPage
                            index={pageIndex}
                            fieldPage={formFieldPageList[pageIndex]}
                            formFieldList={formFieldList}
                            fieldBackgroundStyle={fieldBackgroundStyle}
                            handleFieldEntryChange={handleFieldEntryChange}
                            fieldEntryMap={fieldEntryMap}
                            isNextHovered={isNextHovered}
                            setDisableNext={setDisableNext}
                            kaiziqResolveConnection={kaiziqResolveConnection}/>
                    </div>
                    {
                        ((pageIndex + 1) < formFieldPageList.length) && JSON.parse(formFieldPageList[pageIndex]?.details)?.buttonBehaviour?.next?.behaviour?.action !== 'submit' ?
                            <div className={className + '__inner__body__formBody__form__page__inner__buttons width-wfa flex-row-center-space-between'}>
                                <div className={className + '__inner__body__formBody__form__page__inner__buttons__left flex-row-center-1vw-gap'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__left__startAgain standard-link'}
                                        onClick={reportAnotherClick}>
                                        Clear Form
                                    </div>
                                </div>
                                <div className={className + '__inner__body__formBody__form__page__inner__count grey-text'}>
                                    Page: {pageIndex + 1} / {formFieldPageList.length}
                                </div>
                                <div className={className + '__inner__body__formBody__form__page__inner__buttons__right flex-row-center-1vw-gap'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__right__back '}>
                                        <BorderWhiteButtonV2
                                            text='Back'
                                            disabled={false}
                                            onClick={handleBackClick}/>
                                    </div>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__right__next '}
                                        onMouseEnter={handleMouseOverNext}
                                        onMouseLeave={handleNextDisabledPopoverClose}>
                                        <BorderWhiteButtonV2
                                            text='Next'
                                            disabled={disableNext}
                                            onClick={handleNextClick}/>
                                    </div>
                                </div>
                            </div>
                        :
                            <div className={className + '__inner__body__formBody__form__page__inner__buttons width-wfa flex-row-center-space-between'}>
                                <div className={className + '__inner__body__formBody__form__page__inner__buttons__left flex-row-center-1vw-gap'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__left__startAgain standard-link'}
                                        onClick={reportAnotherClick}>
                                        Clear Form
                                    </div>
                                </div>
                                <div className={className + '__inner__body__formBody__form__page__inner__count grey-text'}>
                                    Page: {pageIndex + 1} / {formFieldPageList.length}
                                </div>
                                <div className={className + '__inner__body__formBody__form__page__inner__buttons__right flex-row-center-1vw-gap'}>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__right__back '}>
                                        <BorderWhiteButtonV2
                                            text='Back'
                                            disabled={false}
                                            onClick={handleBackClick}/>
                                    </div>
                                    <div className={className + '__inner__body__formBody__form__page__inner__buttons__right__next '}>
                                        <BorderBlueButtonV2
                                            text='Submit'
                                            disabled={disableSubmit || disableNext}
                                            onClick={handleSubmitForm}/>
                                    </div>
                                </div>
                            </div>
                    }
                </div>
            </div>
        } else if(pageType === 'pageLoader') {
            pageBody = <div className={className + '__inner__body__formBody__form__page flex-column-center-2vh-gap width-wfa'}>
                <div className={className + '__inner__body__formBody__form__page__loaders flex-column-center-2vh-gap'}>
                    <LeapFrogLoader className={className + '__inner__body__formBody__form__page__loaders__loader default-loader'} color='inherit'/>
                </div>
            </div>
        }
        return pageBody;
    }

    return (
        <div className={className}
            style={{cursor: cursor}}>
            <TopPanel logo text={kaiziqResolveConnection?.title}/>
            <div className={className + '__inner height-wfa'}>
                {
                    cursor === 'wait' && (!kaiziqResolveConnection || Object.keys(kaiziqResolveConnection).length === 0) &&
                        <div className={className + '__inner__body'}>
                            <LeapFrogLoader className={className + '__inner__body__loader default-loader'}/>
                        </div>
                }
                {
                    cursor === 'auto' && (!kaiziqResolveConnection || Object.keys(kaiziqResolveConnection).length === 0) &&
                        <div className={className + '__inner__body '}>
                            <div className={className + '__inner__body__notFound flex-column-center-2vh-gap'}>
                                <FormNotFound className={className + '__inner__body__notFound__graphic'}/>
                                <div className={className + '__inner__body__notFound__t1 standard-text-large'}>
                                    This Form was not found!
                                </div>
                                <div className={className + '__inner__body__notFound__t2 standard-link'} onClick={handleSearchClick}>
                                    Go to the Search Page
                                </div>
                            </div>
                        </div>
                }
                {
                    !!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) != 0 &&
                        <div className={className + '__inner__body height-wfa'}>
                            <div className={className + '__inner__body__formBody flex-row-flex-start-1vw-gap height-wfa'} >
                                <div className={className + '__inner__body__formBody__form flex-column-flex-start-2vh-gap width-wfa'}>
                                    {
                                        cursor === 'wait' ?
                                            <div className={className + '__inner__body__formBody__form__loader'}>
                                                <LeapFrogLoader className={className + '__inner__body__formBody__form__loader__l1 default-loader'} color='inherit'/>
                                            </div>
                                            :
                                                (
                                                    cursor === 'auto' && (!!ticketCreated && Object.keys(ticketCreated).length !== 0) &&
                                                        <div className={className + '__inner__body__formBody__form__created flex-column-center-2vh-gap width-wfa'}>
                                                            <div className={className + '__inner__body__formBody__form__created__inner flex-column-center width-wfa'}>
                                                                <div className={className + '__inner__body__formBody__form__created__t1 standard-text-large'}>
                                                                    Your submission has been recorded!
                                                                </div>
                                                                <HighFive className={className + '__inner__body__formBody__form__created__graphic'}/>
                                                                {
                                                                    details?.settings?.defaults?.generateTicketId &&
                                                                        <div className={className + '__inner__body__formBody__form__created__t2 flex-row-center-1vw-gap'}>
                                                                            Your Ticket ID:
                                                                            <div className={className + '__inner__body__formBody__form__created__tag semi-bold-text standard-link '} onClick={handleTicketIdClick}>
                                                                                {ticketCreated?.ticketId}
                                                                            </div>
                                                                        </div>
                                                                }
                                                                <div className={className + '__inner__body__formBody__form__created__button standard-link'} onClick={reportAnotherClick}>
                                                                    Report Another
                                                                </div>
                                                            </div>
                                                        </div>
                                                )
                                    }
                                    {
                                        cursor !== 'wait' && !(!!ticketCreated && Object.keys(ticketCreated)?.length !== 0) &&
                                            <>
                                                {populatePage()}
                                            </>
                                    }
                                </div>
                            </div>
                        </div>
                }
            </div>
            <Popover
                id='mouse-over-next'
                sx={{
                    pointerEvents: 'none',
                }}
                open={Boolean(anchorNextDisabledPopover)}
                anchorEl={anchorNextDisabledPopover}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                onClose={handleNextDisabledPopoverClose}
                disableRestoreFocus>
                <div className={className + '__nextPopover '}>
                    <div className={className + '__nextPopover__inner'}>
                        Please fill in all required fields (Marked in Red)
                    </div>
                </div>
            </Popover>
        </div>
    );
}
